import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Divider, Space, Button, Tooltip, Message } from '@arco-design/web-react';
import { IconSearch } from '@arco-design/web-react/icon';
import { enterWorkbenchSurface } from './BasicLayout/workbenchSurface';
import { useAuthStore } from '../store/auth';
import { useAgentStore } from '../store/agent';
import { usePhoneStore } from '../store/phone';
import { useWorkbenchBadge } from '../store/workbenchBadge';
import { useCollectGate } from '../store/collectGate';
import { useAdminWs } from '../pages/workbench/ws';
import ChangePasswordModal from '../components/ChangePasswordModal';
import SummaryModal from '../components/SummaryModal';
import OutboundResultModal from '../components/OutboundResultModal';
import ErrorBoundary from '../components/ErrorBoundary';
import NotificationBell from '../components/NotificationBell';
import ModuleMapModal from '../components/ModuleMapModal';
import { playAlertSound, startTitleFlash, stopTitleFlash } from '../utils/ringAlert';
import { recentAnnouncements } from '../api/monitor';
import { abandonAgentCallOnUnload } from '../api/calls';
import RegistrySidebar from './RegistrySidebar';
import QuickDialBar from './header/QuickDialBar';
import AgentStatusTrigger from './header/AgentStatusTrigger';
import ChannelToggles from './header/ChannelToggles';
import CollectSendButton from './header/CollectSendButton';
import SupportRequestButton from './header/SupportRequestButton';
import HeaderUser from './header/HeaderUser';
import ThemeDrawer from './header/ThemeDrawer';
import FloatStack from './BasicLayout/FloatStack';
import { isFullBleed } from './BasicLayout/fullBleed';
import SupportAlertRail from '../pages/support/SupportAlertRail';

const { Header, Content } = Layout;

/**
 * 全局布局框架（BasicLayout）：坐席/管理台共用的外框。
 * 职责收敛为「状态编排 + 组合」：
 *   - 布局：侧栏导航 RegistrySidebar / 顶栏（QuickDialBar、AgentStatusTrigger、ChannelToggles、
 *     NotificationBell、快捷查询、HeaderUser 各自独立组件） / 内容 Outlet（ErrorBoundary）
 *   - 生命周期：坐席档案装载、SIP 软电话注册、管理台 WS 订阅（坐席状态/CDR/通知/广播）、
 *     心跳续租与关页离线兜底
 *   - 全局挂载：挂断小结、外呼结果、改密、模块地图、高危风险上报卡
 * 顶栏软电话条、状态切换器、渠道开关、用户菜单等 UI 与内聚状态均已下沉 header/ 子组件。
 */


export default function BasicLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuthStore();
  const agent = useAgentStore();
  /**
   * 内容区滚动容器（`.bcc-content` 自身 `overflow:auto`）。Arco Layout.Content 是 forwardRef，
   * ref 直接落在该节点上——路由切换必须手动归零，浏览器与 react-router 都不会重置它
   * （表现为：长列表滚到底后切页，新页面从中部开始显示）。
   */
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 只按 pathname 复位：页内 query 变化（Tab 切换、深链参数）不重挂载页面，也不该夺走滚动位置
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [location.pathname]);

  // W5-4：首次登录 must_change_password=true → 全屏强制改密（不可关闭/跳过）；
  // 持久化登录态刷新后同样强制，改密成功才解除
  useEffect(() => {
    if (user?.must_change_password) useAuthStore.getState().openPwdModal(true);
  }, [user?.must_change_password]);

  useEffect(() => {
    if (user?.id) {
      agent.load(user.id);
    }
  }, [user?.id, agent.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // 全局软电话（W2-9）：SIP 注册生命周期提升到 Layout 层——登录坐席账号即注册，
  // 离开工作台页面不断线；登出（token 清空）/非坐席账号（无分机）则注销
  useEffect(() => {
    if (!token) {
      void usePhoneStore.getState().unregister();
      // 登出同步复位坐席档案：否则同页换号登录会沿用上一账号的分机/坐席ID
      // （软电话注册成旧分机，CDR/会话全部记到旧坐席名下，已实测复现）
      useAgentStore.getState().reset();
      // 会话角标同样按账号隔离：不清的话同页换号登录会看到上一账号残留的角标
      useWorkbenchBadge.getState().clear();
      // 收号访问门同理：换号后不得沿用上一账号的验证结果
      useCollectGate.getState().reset();
      return;
    }
    if (!agent.loaded) return;
    // 接听设备 = 外部话机（agent.phone_mode=desk）：不注册软电话。
    // 同一分机若同时以软电话在线，引擎会优先振铃软电话（WSS 腿优先），外部话机永远收不到来电；
    // 这里显式注销以清掉「软电话 → 外部话机」切换前残留的注册，租约/示闲/弹屏不受影响。
    if (agent.phoneMode === 'desk') {
      void usePhoneStore.getState().unregister();
      return;
    }
    const ext = agent.extension || user?.extension;
    const pwd = agent.sipPassword || user?.sipPassword;
    void usePhoneStore.getState().register(ext, pwd);
  }, [token, agent.loaded, agent.extension, agent.sipPassword, agent.phoneMode, user?.extension, user?.sipPassword]);

  // 帮助·模块功能地图弹窗（顶栏「帮助」入口）
  const [helpOpen, setHelpOpen] = useState(false);

  // 快捷键 Ctrl/Cmd + M：打开/关闭「快捷查询（模块功能地图）」
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        setHelpOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 高危风险上报弹窗（notify level=critical）：阻断式提醒 + 提示音；每次上报独立弹出、不节流
  const [riskAlert, setRiskAlert] = useState<{ title: string; body?: string; refType?: string; refID?: string } | null>(null);

  /**
   * 普通通知 toast 去重（title → 最近弹出时刻）：分配提醒这类通知会在短时间内成串到达
   * （同一会话重复上报 / 短时间内多条同名访客会话接入），逐条弹会糊成"同一句提示弹一摞"。
   * 同标题的通知在窗口内只弹一次；通知铃与未读角标照常刷新，明细不丢（铃铛里仍逐条可查）。
   */
  const NOTIFY_TOAST_DEDUP_MS = 10000;
  const notifyToastAtRef = useRef<Map<string, number>>(new Map());

  // ---------- 挂断触发服务小结（全局：软电话在 Layout 层，任意页面挂断都弹） ----------
  const [summaryOpen, setSummaryOpen] = useState(false);
  /** cdr_written 推送的本通 CDR（可能晚于弹窗几百 ms 到达） */
  const [cdrEvent, setCdrEvent] = useState<{ id: number; at: number }>();
  /** ai_ticket_created 推送的自动建单（v1.8.1：挂断弹窗展示横幅 + 查看跳转） */
  const [autoTicket, setAutoTicket] = useState<{
    cdr_id: number;
    /** 工单号口径全站统一为工单主键 id（主列表「工单号」列即此值）；不要改用 ticket_no */
    ticket_id: number;
    type: string;
    priority: string;
    source: string;
    intent_code: string;
    at: number;
  }>();
  const phoneStatus = usePhoneStore((s) => s.status);
  const mockPrototype = import.meta.env.DEV && new URLSearchParams(window.location.search).get('mock') === '1';
  const effectiveAgentState = mockPrototype && ['ringing-in', 'ringing-out', 'in-call'].includes(phoneStatus) ? 'busy' : agent.state;

  // 进入坐席工作台即清空会话角标：会话列表、未读消息都在眼前，角标再留着就是噪音
  useEffect(() => {
    if (location.pathname === '/workbench') useWorkbenchBadge.getState().clear();
  }, [location.pathname]);

  /**
   * 接通即回工作台（电话）：软电话应答（phone status → in-call）与信令侧接通
   * （`call_answered{leg:agent}`，外部话机接听只有这一路信号）都要跳。
   *
   * 判据是**状态迁移**而非"当前是否在通话中"：若写成后者，坐席通话中主动去别的页面查资料
   * 会被立刻弹回工作台，等于把人锁在通话页里。
   */
  const prevStatusForJump = useRef(phoneStatus);
  useEffect(() => {
    const prev = prevStatusForJump.current;
    prevStatusForJump.current = phoneStatus;
    if (!agent.agentId) return;
    if (phoneStatus !== 'in-call' || prev === 'in-call') return;
    if (location.pathname === '/workbench') return;
    // 统一入口：先记下要展示的界面（页面挂载时据此恢复），再跳工作台
    enterWorkbenchSurface('phone');
    navigate('/workbench');
  }, [phoneStatus, agent.agentId, location.pathname, navigate]);

  const prevStatus = useRef(phoneStatus);
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = phoneStatus;
    if (mockPrototype) return;
    if (prev === 'in-call' && phoneStatus !== 'in-call') {
      // 盲转（REFER）完成后本腿结束：客户腿已转出由目标坐席继续服务，本通不算完整通话，
      // 后端转出分支不预写本腿 CDR——此时弹服务小结只会查出空记录，直接跳过（复位标记）
      if (usePhoneStore.getState().transferredOut) {
        usePhoneStore.setState({ transferredOut: false });
        return;
      }
      setSummaryOpen(true); // ringing-out 未接通不弹（未经历 in-call）
    }
  }, [phoneStatus, mockPrototype]);

  // 通话中离开保护：软电话（SIP.js + WebRTC）跑在本页 JS 上下文里，刷新/关标签/跳外链会
  // 销毁 UA 与媒体会话 → 通话直接中断，而服务端只看到一次正常挂断，坐席侧毫无预警。
  // 浏览器只允许「拦一次确认框」（自定义文案已被现代浏览器忽略，仅作语义占位）。
  useEffect(() => {
    if (phoneStatus !== 'in-call') return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '通话进行中，刷新或关闭页面会中断当前通话';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phoneStatus]);

  // 页面真的要走（pagehide）时主动告诉服务端放弃本通：确认框被取消不会触发 pagehide，
  // 因此这里只会「确实离开」时上报；进 bfcache（persisted）页面还会回来，跳过不误伤。
  // 服务端据此立刻挂断坐席腿走正常收尾（已接通→满意度/挂断客户腿，未接通→客户腿重入队），
  // 不等 FS 自行感知 WS 断开，也不会留下「状态卡通话中、后续呼叫接不到」的窗口。
  useEffect(() => {
    if (!['ringing-in', 'ringing-out', 'in-call'].includes(phoneStatus)) return;
    const onPageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return; // 进往返缓存：页面可能被恢复，通话未真正销毁
      abandonAgentCallOnUnload();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [phoneStatus]);

  /**
   * 当前来电 uuid（WS 采集，挂断即清）：振铃态转接要把它交给后端 /assist/actions/execute。
   * 采集必须在常驻的顶栏布局里做——转接面板是点开才挂载的，面板内只能读到"最近一条事件"，
   * 而派单后紧跟广播的 queue_update / agent_state 会把它顶掉，uuid 恒取不到（转接恒报"未定位到当前来电"）。
   * 用 ref 承接（不触发重渲染）：订阅方每收到一条事件都会渲染，顶栏不该跟着转写事件重渲染。
   */
  const ringCallUUIDRef = useRef('');
  /** 已触发过「接通回工作台」的通话 uuid：同一通话的 call_answered 可能重复到达，去重防反复弹回 */
  const answeredJumpUUIDRef = useRef('');
  /** 当前路由：WS 回调据此判断人是否已在工作台（人在工作台上时会话更新实时可见，不记角标） */
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  /** 记一次「本人会话有更新」角标（左菜单数字角标，见 store/workbenchBadge） */
  const bumpBadgeIfAway = (sessionId: number) => {
    if (pathnameRef.current !== '/workbench') useWorkbenchBadge.getState().bump(sessionId);
  };
  // 订阅管理台 WS：服务端坐席状态机（振铃/通话中/话后）实时同步到顶栏
  const { lastEvent, connected, send: wsSend } = useAdminWs((e) => {
    // 来电归属：caller-leg 事件无 agent_id（只投班长/管理员），带 agent_id 的必须等于本坐席
    if (e.type === 'call_ringing' || e.type === 'call_answered') {
      const u = String(e.data?.uuid || '');
      const aid = Number(e.data?.agent_id || 0);
      if (u && (aid === 0 || !agent.agentId || aid === agent.agentId)) ringCallUUIDRef.current = u;
    } else if (e.type === 'call_hangup') {
      const u = String(e.data?.uuid || '');
      if (!u || u === ringCallUUIDRef.current) ringCallUUIDRef.current = '';
    }
    // 接通即回工作台（信令侧）：外部话机接听、zitch 单腿外呼只有这一路「接通」信号
    // （软电话应答由上面 phone status → in-call 的迁移负责）
    if (e.type === 'call_answered') {
      const u = String(e.data?.uuid || '');
      const aid = Number(e.data?.agent_id || 0);
      if (String(e.data?.leg || '') === 'agent' && aid > 0 && aid === agent.agentId && u && u !== answeredJumpUUIDRef.current) {
        answeredJumpUUIDRef.current = u;
        enterWorkbenchSurface('phone');
        if (pathnameRef.current !== '/workbench') navigate('/workbench');
      }
    }
    // 在线会话：派到本人的新会话（ACD 自动分配 / 转接给本人）直接回工作台办理；
    // 本人承办会话的新访客消息只在离开工作台期间记一次角标。
    if (e.type === 'chat_new') {
      const sid = Number(e.data?.session_id ?? e.data?.sessionId ?? 0);
      const assignedToMe = e.data?.assigned === true && agent.agentId && Number(e.data?.agent_id || 0) === agent.agentId;
      if (sid > 0 && assignedToMe) {
        bumpBadgeIfAway(sid);
        enterWorkbenchSurface('online');
        if (pathnameRef.current !== '/workbench') navigate('/workbench');
      }
    }
    if (e.type === 'chat_message') {
      const sid = Number(e.data?.session_id ?? e.data?.sessionId ?? 0);
      const fromVisitorToMe =
        String(e.data?.sender_type || '') === 'visitor' && agent.agentId && Number(e.data?.agent_id || 0) === agent.agentId;
      if (sid > 0 && fromVisitorToMe) bumpBadgeIfAway(sid);
    }
    // 会话已结束（含访客长时间无消息自动关闭）：从角标里摘掉，角标不挂已关闭会话
    if (e.type === 'chat_closed') {
      const sid = Number(e.data?.session_id ?? e.data?.sessionId ?? 0);
      if (sid > 0) useWorkbenchBadge.getState().drop(sid);
    }
    if (e.type === 'agent_state' && e.data?.agent_id === agent.agentId && e.data?.state) {
      agent.applyRemoteState(e.data.state, e.data.break_reason_id, e.data.break_reason);
    }
    // CDR 落库推送：挂断小结弹窗按 id 精确定位本通（仅本人坐席的腿；WS 丢失时弹窗回退基线轮询）
    if (e.type === 'cdr_written' && e.data?.cdr_id && e.data?.agent_id === agent.agentId) {
      setCdrEvent({ id: Number(e.data.cdr_id), at: Date.now() });
    }
    // AI 自动建单完成（v1.8.1）：挂断弹窗展示「AI 已建工单」横幅（按本人坐席目标投递）
    if (e.type === 'ai_ticket_created' && e.data?.cdr_id && e.data?.agent_id === agent.agentId) {
      setAutoTicket({
        cdr_id: Number(e.data.cdr_id),
        ticket_id: Number(e.data.ticket_id || 0),
        type: String(e.data.type || ''),
        priority: String(e.data.priority || ''),
        source: String(e.data.source || ''),
        intent_code: String(e.data.intent_code || ''),
        at: Date.now(),
      });
    }
    // 公告广播（v1.5 P4）：聚合广播给全部在线坐席
    if (e.type === 'broadcast' && e.data?.content) {
      Message.warning({ content: `📢 ${e.data.content}`, duration: 10000 });
    }
    // 公告中心：不在线实时弹窗（改为坐席签入时检查）；发布会话同步仍经 /notices/pending 由签入触发
    // 站内通知（v1.2 通知中心）：WS notify 已由服务端按 user 目标投递 → 刷新通知铃 + Toast
    // 风险上报（所有等级统一 critical）：大卡片 + 提示音 + 标签页标题闪烁；其余通知维持 toast
    if (e.type === 'notify') {
      const ref = (window as any).__refreshNotifications;
      if (typeof ref === 'function') ref();
      if (e.data?.level === 'critical') {
        const title = String(e.data?.title || '高危风险上报');
        setRiskAlert({
          title,
          body: e.data?.body ? String(e.data.body) : undefined,
          refType: e.data?.ref_type ? String(e.data.ref_type) : undefined,
          refID: e.data?.ref_id ? String(e.data.ref_id) : undefined,
        });
        playAlertSound(3);
        startTitleFlash(`【风险上报】${title}`);
      } else {
        const title = e.data?.title ? String(e.data.title) : '';
        if (title) {
          const now = Date.now();
          const seen = notifyToastAtRef.current;
          const last = seen.get(title) ?? 0;
          if (now - last > NOTIFY_TOAST_DEDUP_MS) {
            seen.set(title, now);
            Message.info({ content: `通知：${title}`, duration: 4000 });
          }
          // 顺带清理窗口外的旧键：长驻页面下 Map 不随通知量无界增长
          if (seen.size > 50) {
            seen.forEach((t, k) => { if (now - t > NOTIFY_TOAST_DEDUP_MS) seen.delete(k); });
          }
        }
      }
    }
    // v1.10 流程状态同步：任务创建/实例结束 → 刷新通知铃与待办（他人处理完后本页即时更新，
    // 配合后端防重守卫与前端 pending 双校验，消灭陈旧待办可 redo 的观感）
    if (e.type === 'workflow_task_created' || e.type === 'workflow_instance_finished') {
      const ref = (window as any).__refreshNotifications;
      if (typeof ref === 'function') ref();
      const rt = (window as any).__refreshTodos;
      if (typeof rt === 'function') rt();
    }
  });

  // 公告补拉（v1.5 P4 可靠化）：WS 连接建立/重连后拉最近 10 分钟公告弹 toast（已提示的去重）
  const shownAnnouncements = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!connected) return;
    recentAnnouncements(10)
      .then((list: any[]) => {
        (list || []).forEach((a) => {
          if (a?.content && !shownAnnouncements.current.has(Number(a.id))) {
            shownAnnouncements.current.add(Number(a.id));
            Message.warning({ content: `📢 ${a.content}`, duration: 10000 });
          }
        });
      })
      .catch(() => {});
  }, [connected]);

  // 自愈轮询 + 心跳续租：WS 事件可能丢失（断连/旧缓存页），每 15s 对账一次并刷新存活租约；
  // 顺带做软电话自愈（healIfNeeded）：坐席在线但软电话注册丢失/WS 断线未恢复时静默重注册，
  // 收窄「系统在线但 FS 未注册」的假在线窗口（FS 重启后不再等 sip.js 的下一个注册刷新周期）。
  useEffect(() => {
    if (!agent.agentId) return;
    agent.refreshState(); // 立即首次心跳（续租+对账，不等 15s 间隔）
    void usePhoneStore.getState().healIfNeeded();
    const timer = setInterval(() => {
      agent.refreshState();
      void usePhoneStore.getState().healIfNeeded();
    }, 15000);
    // 后台标签页 setInterval 会被浏览器节流/冻结，导致租约过期被 30s 巡检置 offline；
    // 回到前台时立即续租一次，避免「恢复可见时已变离线」。
    const onVisible = () => {
      if (document.visibilityState === 'visible' && useAgentStore.getState().state !== 'offline') {
        agent.refreshState();
        void usePhoneStore.getState().healIfNeeded();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [agent.agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 关标签页/断网兜底：fetch keepalive 发出离线请求（best-effort；刷新/回退不置离线，
  // 否则每次 F5 后新会话读到 offline、且心跳跳过续租，坐席「卡死离线」无法自愈）
  useEffect(() => {
    const onUnload = () => {
      const nav = (performance.getEntriesByType('navigation') as any[])[0];
      if (nav?.type === 'reload' || nav?.type === 'back_forward') return;
      const { agentId, state } = useAgentStore.getState();
      if (agentId && state !== 'offline') {
        fetch(`/api/v1/agents/${agentId}/state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ state: 'offline' }),
          keepalive: true,
        });
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [token]);

  return (
    // 注意：RegistrySidebar 为自定义组件，包裹了 Arco Sider——Arco Layout 仅对「直接子级 Sider」
    // 注入 onSiderMount 自动激活 has-sider（横向布局），隔层后自动推断失效，
    // 必须显式传 hasSider（官方 prop），否则默认纵向会把 Header/内容推到页面下方
    <Layout style={{ height: '100vh' }} hasSider>
      <RegistrySidebar />
      <Layout>
        <Header className="bcc-header">
          <Space size={16} align="center">
            {agent.agentId && (
              <>
                <QuickDialBar ringCallUUIDRef={ringCallUUIDRef} />
                <AgentStatusTrigger forcedState={mockPrototype ? effectiveAgentState : undefined} />
                <ChannelToggles />
                {/* 收号字段下发入口（CollectSendButton）：举手（请求组长支援）已改为常显，见下方顶栏。
                    传入顶栏这条常驻 WS 的 send：会话 Tab 下收号走 chat_flow_op 需经管理台 WS 下发。
                    「拉铃 / 拉铃处理」（DutyBellGroup）按当前版式**不上顶栏**：件数恒为 0（拉铃接口未接），
                    两个恒 0 的入口只占位不产出；组件与样式保留，接口就绪后再挂回顶栏。 */}
                <CollectSendButton wsSend={wsSend} />
              </>
            )}
            {agent.agentId && <Divider type="vertical" style={{ margin: 0 }} />}
            {/* 班次不动顶栏：时段在用户名下拉（layouts/header/HeaderUser） */}
            {/* 举手（请求支援）常显：不绑定坐席档案的账号（组长/值班长/管理员）也要能发起 */}
            <SupportRequestButton />
            <NotificationBell />
            <Tooltip content="快捷查询（模块功能地图）· Ctrl+M">
              <Button
                type="text"
                shape="circle"
                icon={<IconSearch />}
                onClick={() => setHelpOpen(true)}
                aria-label="快捷查询"
              />
            </Tooltip>
            <HeaderUser />
          </Space>
        </Header>
        <Content className="bcc-content" ref={contentRef}>
          {/* 全局渲染错误边界（白屏防护）：任何面板渲染抛错降级为错误卡片而非整树卸载。
              resetKey=pathname：切路由即复位，否则一次崩溃会粘住后续所有页面直到整页重载 */}
          <ErrorBoundary resetKey={location.pathname}>
            {/* 内容宽度档（界面设置 → 内容宽度）：满幅页面（工作台）不套容器，
                其余页面按 --bcc-content-max 约束并居中（默认 none = 通栏） */}
            {isFullBleed(location.pathname) ? (
              <Outlet />
            ) : (
              <div className="bcc-content-wrap">
                <Outlet />
              </div>
            )}
          </ErrorBoundary>
        </Content>
      </Layout>
      {/* 挂断服务小结弹窗（全局：任意页面挂断都弹；仅坐席账号） */}
      {agent.agentId && !mockPrototype && (
        <SummaryModal
          visible={summaryOpen}
          cdrEvent={cdrEvent}
          autoTicketEvent={autoTicket}
          onClose={() => setSummaryOpen(false)}
        />
      )}
      {/* v1.10 外呼结果填写面板（人工外呼/AI 转人工坐席承接；随小结弹窗旁挂出，仅坐席账号） */}
      {agent.agentId && !mockPrototype && (
        <OutboundResultModal
          visible={summaryOpen}
          cdrEvent={cdrEvent}
          onClose={() => setSummaryOpen(false)}
        />
      )}
      {/* 全局改密 Modal（W5-4）：强制/个人入口共用 */}
      <ChangePasswordModal />
      {/* 界面设置抽屉（主题配色）：入口在侧栏底栏，实例只此一处（store/theme.ts 管开合） */}
      <ThemeDrawer />
      {/* 帮助·模块功能地图 */}
      <ModuleMapModal visible={helpOpen} onClose={() => setHelpOpen(false)} />
      <FloatStack
        riskAlert={riskAlert}
        onViewRisk={() => {
          const refID = riskAlert?.refID;
          stopTitleFlash();
          setRiskAlert(null);
          navigate(refID ? `/calls?id=${refID}` : '/calls');
        }}
      />
      {/* 举手报告右侧常驻卡（组长 / 值班长，任意页面）：演示数据，不连后端 */}
      <SupportAlertRail />
    </Layout>
  );
}
